import { useState, useEffect, useCallback, useRef } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, ExternalLink, Upload, X } from "lucide-react";
import { toast } from "sonner";

import {
  useCreateCollection,
  useUpdateCollection,
  useUploadCollectionBanner,
  useCollections,
  type Collection,
} from "./hooks";
import { CollectionProductsTab } from "./CollectionProductsTab";
import { useAuthStore } from "@/lib/store/authStore";

/** Resolve a relative file URL (e.g. /files/xyz.png) to an absolute tenant server URL */
function resolveTenantFileUrl(
  fileUrl: string | null | undefined,
  tenantServerUrl: string | null
): string | null {
  if (!fileUrl) return null;
  if (fileUrl.startsWith("http")) return fileUrl;
  if (fileUrl.startsWith("/") && tenantServerUrl) return `${tenantServerUrl}${fileUrl}`;
  return fileUrl;
}

// Sort order options matching backend _SORT_ORDER_MAP (ASCII hyphens)
const SORT_OPTIONS = [
  { value: "Manual", label: "Manual" },
  { value: "A-Z", label: "A–Z" },
  { value: "Z-A", label: "Z–A" },
  { value: "Price Low to High", label: "Price Low to High" },
  { value: "Price High to Low", label: "Price High to Low" },
  { value: "Newest First", label: "Newest First" },
] as const;

function slugify(text: string): string {
  if (!text) return "";
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/[-\s]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

interface CollectionDrawerProps {
  open: boolean;
  onClose: () => void;
  collection: Collection | null; // null = create mode
  tenantKey: string | null;
}

export function CollectionDrawer({
  open,
  onClose,
  collection,
  tenantKey,
}: CollectionDrawerProps) {
  const isEdit = !!collection;
  const storefrontUrl = useAuthStore((s) => s.storefrontUrl);
  const tenantServerUrl = useAuthStore((s) => s.tenantServerUrl);
  const resolvedStorefrontUrl =
    storefrontUrl || import.meta.env.VITE_STOREFRONT_URL || "";

  // Form state
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [description, setDescription] = useState("");
  const [bannerImage, setBannerImage] = useState<string | null>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [parentCollection, setParentCollection] = useState("");
  const [sortOrder, setSortOrder] = useState("Manual");
  const [isVisible, setIsVisible] = useState(true);

  const titleRef = useRef<HTMLInputElement>(null);

  // Mutations
  const createMutation = useCreateCollection(tenantKey);
  const updateMutation = useUpdateCollection(tenantKey);
  const uploadBannerMutation = useUploadCollectionBanner(tenantKey);
  const isSaving =
    createMutation.isPending || updateMutation.isPending || uploadBannerMutation.isPending;

  // Parent collection options
  const { data: allCollections } = useCollections(tenantKey);
  const parentOptions = (allCollections ?? []).filter(
    (c) => c.name !== collection?.name
  );

  // Reset form when drawer opens/closes or collection changes
  useEffect(() => {
    if (open) {
      if (collection) {
        setTitle(collection.title);
        setSlug(collection.slug);
        setSlugManuallyEdited(true); // Don't auto-gen slug in edit mode
        setDescription(collection.description || "");
        setBannerImage(collection.banner_image || null);
        setBannerFile(null);
        setParentCollection(collection.parent_collection || "");
        setSortOrder(collection.sort_order || "Manual");
        setIsVisible(collection.is_visible);
      } else {
        setTitle("");
        setSlug("");
        setSlugManuallyEdited(false);
        setDescription("");
        setBannerImage(null);
        setBannerFile(null);
        setParentCollection("");
        setSortOrder("Manual");
        setIsVisible(true);
      }
    }
  }, [open, collection]);

  // Auto-focus title on open
  useEffect(() => {
    if (open) {
      // Small delay for animation
      const timer = setTimeout(() => titleRef.current?.focus(), 350);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Slug auto-generation on title blur
  const handleTitleBlur = useCallback(() => {
    if (!slugManuallyEdited && title.trim()) {
      setSlug(slugify(title));
    }
  }, [title, slugManuallyEdited]);

  const handleSlugChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSlugManuallyEdited(true);
      // Only allow valid slug characters
      const value = e.target.value
        .toLowerCase()
        .replace(/[^a-z0-9_-]/g, "");
      setSlug(value);
    },
    []
  );

  const handleSave = useCallback(async () => {
    if (!title.trim()) {
      toast.error("Title is required.");
      return;
    }

    try {
      if (isEdit && collection) {
        // Upload banner file if a new one was selected
        let bannerUrl = bannerImage;
        if (bannerFile) {
          const upload = await uploadBannerMutation.mutateAsync({
            docName: collection.name,
            file: bannerFile,
          });
          bannerUrl = upload.file_url;
        }

        await updateMutation.mutateAsync({
          collection_name: collection.name,
          title: title.trim(),
          description: description.trim() ?? "",
          banner_image: bannerUrl ?? "",
          parent_collection: parentCollection || undefined,
          sort_order: sortOrder,
          is_visible: isVisible ? 1 : 0,
        });
        toast.success(`Collection '${title.trim()}' saved.`);
      } else {
        const finalSlug = slug.trim() || slugify(title);
        const created = await createMutation.mutateAsync({
          title: title.trim(),
          slug: finalSlug || undefined,
          description: description.trim() || undefined,
          parent_collection: parentCollection || undefined,
          sort_order: sortOrder,
          is_visible: isVisible ? 1 : 0,
        });

        // Upload banner file after creation (need the doc name)
        if (bannerFile && created.name) {
          const upload = await uploadBannerMutation.mutateAsync({
            docName: created.name,
            file: bannerFile,
          });
          await updateMutation.mutateAsync({
            collection_name: created.name,
            banner_image: upload.file_url,
          });
        }

        toast.success(`Collection '${title.trim()}' created.`);
      }
      onClose();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Something went wrong.";
      toast.error(message);
    }
  }, [
    title,
    slug,
    description,
    bannerImage,
    bannerFile,
    parentCollection,
    sortOrder,
    isVisible,
    isEdit,
    collection,
    createMutation,
    updateMutation,
    uploadBannerMutation,
    onClose,
  ]);

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" aria-describedby="drawer-description">
        <SheetHeader>
          <SheetTitle>
            {isEdit ? `Edit Collection — ${collection.title}` : "New Collection"}
          </SheetTitle>
          <SheetDescription id="drawer-description">
            {isEdit
              ? "Update collection details or manage products."
              : "Create a new browsable collection for your storefront."}
          </SheetDescription>
        </SheetHeader>

        {isEdit ? (
          <Tabs defaultValue="details" className="flex flex-1 flex-col overflow-hidden">
            <div className="px-6">
              <TabsList className="w-full">
                <TabsTrigger value="details" className="flex-1">
                  Details
                </TabsTrigger>
                <TabsTrigger value="products" className="flex-1">
                  Products
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent
              value="details"
              className="flex-1 overflow-y-auto px-6 pb-4"
            >
              <DetailsForm
                title={title}
                slug={slug}
                description={description}
                bannerImage={bannerImage}
                bannerFile={bannerFile}
                parentCollection={parentCollection}
                sortOrder={sortOrder}
                isVisible={isVisible}
                parentOptions={parentOptions}
                titleRef={titleRef}
                onTitleChange={setTitle}
                onTitleBlur={handleTitleBlur}
                onSlugChange={handleSlugChange}
                onDescriptionChange={setDescription}
                onBannerFileChange={setBannerFile}
                onBannerImageClear={() => { setBannerImage(null); setBannerFile(null); }}
                onParentCollectionChange={setParentCollection}
                onSortOrderChange={setSortOrder}
                onIsVisibleChange={setIsVisible}
                isEdit={isEdit}
                storefrontBaseUrl={resolvedStorefrontUrl}
                tenantServerUrl={tenantServerUrl}
              />
            </TabsContent>

            <TabsContent
              value="products"
              className="flex-1 overflow-hidden"
            >
              <CollectionProductsTab
                tenantKey={tenantKey}
                collectionName={collection.name}
              />
            </TabsContent>

            {/* Footer for Details tab */}
            <SheetFooter>
              <Button
                variant="outline"
                onClick={onClose}
                disabled={isSaving}
                className="rounded"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={isSaving || !title.trim()}
                className="rounded"
              >
                {isSaving && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save Collection
              </Button>
            </SheetFooter>
          </Tabs>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 pb-4">
              <DetailsForm
                title={title}
                slug={slug}
                description={description}
                bannerImage={bannerImage}
                bannerFile={bannerFile}
                parentCollection={parentCollection}
                sortOrder={sortOrder}
                isVisible={isVisible}
                parentOptions={parentOptions}
                titleRef={titleRef}
                onTitleChange={setTitle}
                onTitleBlur={handleTitleBlur}
                onSlugChange={handleSlugChange}
                onDescriptionChange={setDescription}
                onBannerFileChange={setBannerFile}
                onBannerImageClear={() => { setBannerImage(null); setBannerFile(null); }}
                onParentCollectionChange={setParentCollection}
                onSortOrderChange={setSortOrder}
                onIsVisibleChange={setIsVisible}
                isEdit={false}
                storefrontBaseUrl={resolvedStorefrontUrl}
                tenantServerUrl={tenantServerUrl}
              />
            </div>

            <SheetFooter>
              <Button
                variant="outline"
                onClick={onClose}
                disabled={isSaving}
                className="rounded"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={isSaving || !title.trim()}
                className="rounded"
              >
                {isSaving && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save Collection
              </Button>
            </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ── Details Form (shared between create and edit modes) ────────

interface DetailsFormProps {
  title: string;
  slug: string;
  description: string;
  bannerImage: string | null;
  bannerFile: File | null;
  parentCollection: string;
  sortOrder: string;
  isVisible: boolean;
  parentOptions: Collection[];
  titleRef: React.RefObject<HTMLInputElement | null>;
  onTitleChange: (v: string) => void;
  onTitleBlur: () => void;
  onSlugChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDescriptionChange: (v: string) => void;
  onBannerFileChange: (file: File | null) => void;
  onBannerImageClear: () => void;
  onParentCollectionChange: (v: string) => void;
  onSortOrderChange: (v: string) => void;
  onIsVisibleChange: (v: boolean) => void;
  isEdit: boolean;
  storefrontBaseUrl: string;
  tenantServerUrl: string | null;
}

function DetailsForm({
  title,
  slug,
  description,
  bannerImage,
  bannerFile,
  parentCollection,
  sortOrder,
  isVisible,
  parentOptions,
  titleRef,
  onTitleChange,
  onTitleBlur,
  onSlugChange,
  onDescriptionChange,
  onBannerFileChange,
  onBannerImageClear,
  onParentCollectionChange,
  onSortOrderChange,
  onIsVisibleChange,
  isEdit,
  storefrontBaseUrl,
  tenantServerUrl,
}: DetailsFormProps) {
  const resolvedBannerUrl = resolveTenantFileUrl(bannerImage, tenantServerUrl);
  const previewUrl = bannerFile ? URL.createObjectURL(bannerFile) : resolvedBannerUrl;
  return (
    <div className="space-y-5 pt-4">
      {/* Title */}
      <div className="space-y-2">
        <Label htmlFor="collection-title">Title</Label>
        <Input
          id="collection-title"
          ref={titleRef}
          placeholder="e.g. Coating Products"
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          onBlur={onTitleBlur}
        />
      </div>

      {/* Slug */}
      <div className="space-y-2">
        <Label htmlFor="collection-slug">Slug</Label>
        <Input
          id="collection-slug"
          placeholder="auto-generated-from-title"
          value={slug}
          onChange={onSlugChange}
          disabled={isEdit}
          className={isEdit ? "opacity-60" : ""}
        />
        {slug && (
          <a
            href={`${storefrontBaseUrl}/collections/${slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground font-mono hover:text-foreground transition-colors"
          >
            /collections/{slug}
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="collection-description">Description</Label>
        <Textarea
          id="collection-description"
          placeholder="A short description for this collection..."
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          rows={3}
        />
      </div>

      {/* Banner Image */}
      <div className="space-y-2">
        <Label>Banner Image</Label>
        {previewUrl ? (
          <div className="relative">
            <img
              src={previewUrl}
              alt="Banner preview"
              className="h-32 w-full rounded object-cover border border-border/40"
            />
            <button
              type="button"
              onClick={onBannerImageClear}
              className="absolute top-2 right-2 flex h-6 w-6 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/70"
              aria-label="Remove banner image"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <label className="flex cursor-pointer flex-col items-center gap-2 rounded-md border-2 border-dashed border-muted-foreground/20 p-6 transition-colors hover:border-muted-foreground/40 hover:bg-muted/20">
            <Upload className="h-5 w-5 text-muted-foreground/50" />
            <span className="text-sm text-muted-foreground">
              Click to upload banner image
            </span>
            <span className="text-xs text-muted-foreground/60">
              JPG, PNG, or WebP
            </span>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => onBannerFileChange(e.target.files?.[0] ?? null)}
            />
          </label>
        )}
      </div>

      {/* Parent Collection */}
      <div className="space-y-2">
        <Label>Parent Collection</Label>
        <Select
          value={parentCollection || "__none__"}
          onValueChange={(v) => onParentCollectionChange(v === "__none__" ? "" : v)}
        >
          <SelectTrigger className="h-14 w-full rounded-md border border-border/40 bg-gray-50 dark:bg-gray-900/50 px-5">
            <SelectValue placeholder="None (top-level)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">None (top-level)</SelectItem>
            {parentOptions.map((c) => (
              <SelectItem key={c.name} value={c.name}>
                {c.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Sort Order */}
      <div className="space-y-2">
        <Label>Sort Order</Label>
        <Select value={sortOrder} onValueChange={onSortOrderChange}>
          <SelectTrigger className="h-14 w-full rounded-md border border-border/40 bg-gray-50 dark:bg-gray-900/50 px-5">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Visible toggle */}
      <div className="flex items-center justify-between rounded-md bg-muted/30 p-4">
        <div>
          <p className="text-sm font-medium">Visible on storefront</p>
          <p className="text-xs text-muted-foreground">
            Hidden collections don&apos;t appear in storefront listings or search. Direct links and product URLs keep working.
          </p>
        </div>
        <Switch
          checked={isVisible}
          onCheckedChange={onIsVisibleChange}
          aria-label="Visible on storefront"
        />
      </div>
    </div>
  );
}
